/**
 * Feb-2026 (User-Wunsch 26 May) — Probefahrt-Anfragen-Karte für das
 * Händler-Dashboard.
 *
 * Listet eingehende Probefahrt-Anfragen, zeigt Counter offener Anfragen
 * und ermöglicht Status-Updates (bestätigen / verschoben / abgelehnt /
 * abgeschlossen). Lädt Daten von ``GET /api/dealer/test-ride-requests``.
 */
import { useEffect, useState } from "react";
import { KeyRound, Loader2, RefreshCcw, Mail, Phone, Calendar, MessageSquare, CheckCircle2, XCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import CultureHintCard from "../culture/CultureHintCard";

const STATUS_BADGES = {
    pending:     { label: "Offen", cls: "bg-amber-100 text-amber-800" },
    confirmed:   { label: "Bestätigt", cls: "bg-emerald-100 text-emerald-800" },
    rescheduled: { label: "Verschoben", cls: "bg-blue-100 text-blue-800" },
    cancelled:   { label: "Abgelehnt", cls: "bg-gray-100 text-gray-600" },
    completed:   { label: "Erledigt", cls: "bg-gray-100 text-gray-800" },
};

const STATUS_ACTIONS = [
    { key: "confirmed",   icon: CheckCircle2, label: "Bestätigen", cls: "border-emerald-600 text-emerald-700 hover:bg-emerald-50" },
    { key: "rescheduled", icon: Clock,        label: "Verschieben", cls: "border-blue-600 text-blue-700 hover:bg-blue-50" },
    { key: "cancelled",   icon: XCircle,      label: "Ablehnen", cls: "border-gray-400 text-gray-700 hover:bg-gray-100" },
];

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function TestRideRequestsCard() {
    const [data, setData] = useState({ items: [], count: 0 });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/test-ride-requests");
            setData(r.data);
        } catch { /* silent */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        setBusy(`${id}-${status}`);
        try {
            await api.patch(`/test-ride-requests/${id}/status`, { status });
            toast.success("Status aktualisiert.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update fehlgeschlagen.");
        } finally {
            setBusy(null);
        }
    };

    const items = data.items || [];
    const pendingCount = items.filter((i) => ["pending", "rescheduled"].includes(i.status)).length;

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="test-ride-requests-card">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-[#FFCC00]" />
                    <h3 className="font-display font-bold text-base">Probefahrt-Anfragen</h3>
                    {pendingCount > 0 && (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#111827] text-[#FFCC00] rounded-sm font-mono-num">
                            {pendingCount} offen
                        </span>
                    )}
                </div>
                <button
                    onClick={load}
                    className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1"
                    data-testid="test-rides-refresh"
                >
                    <RefreshCcw className="w-3 h-3" /> Aktualisieren
                </button>
            </div>

            {/* Jul-2026 (User-Wunsch) — Firmen-DNA: Probefahrt-Leitfaden im Arbeitskontext. */}
            <CultureHintCard area="probefahrt" compact />

            {loading ? (
                <div className="flex items-center gap-2 justify-center py-6 text-gray-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Lade Anfragen…
                </div>
            ) : items.length === 0 ? (
                <div className="text-sm text-gray-500 py-6 text-center" data-testid="test-rides-empty">
                    Keine Probefahrt-Anfragen vorhanden. Sobald ein Interessent auf „Probefahrtanfrage" klickt, taucht die Anfrage hier auf.
                </div>
            ) : (
                <ul className="divide-y divide-gray-100 max-h-[40rem] overflow-auto" data-testid="test-rides-list">
                    {items.map((it) => {
                        const badge = STATUS_BADGES[it.status] || STATUS_BADGES.pending;
                        return (
                            <li key={it.id} className="py-3" data-testid={`test-ride-${it.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <button
                                            type="button"
                                            onClick={() => window.open(`/listing/${it.listing_id}`, "_blank", "noopener,noreferrer")}
                                            className="text-sm font-bold truncate text-left hover:underline"
                                            data-testid={`test-ride-listing-${it.id}`}
                                        >
                                            {it.listing_title || "Fahrzeug"}
                                        </button>
                                        <div className="text-xs text-gray-600 mt-0.5">
                                            <strong>{it.buyer_first_name} {it.buyer_last_name}</strong>
                                            <span className="ml-2 inline-flex items-center gap-1"><Mail className="w-3 h-3" /><a href={`mailto:${it.buyer_email}`} className="text-[#2563EB] hover:underline">{it.buyer_email}</a></span>
                                            <span className="ml-2 inline-flex items-center gap-1"><Phone className="w-3 h-3" /><a href={`tel:${it.buyer_phone}`} className="text-[#2563EB] hover:underline">{it.buyer_phone}</a></span>
                                        </div>
                                        {it.preferred_date && (
                                            <div className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
                                                <Calendar className="w-3 h-3" /> Wunsch: <strong className="text-gray-800">{it.preferred_date}</strong>
                                            </div>
                                        )}
                                        {it.message && (
                                            <div className="text-xs text-gray-700 mt-1.5 bg-gray-50 border-l-2 border-gray-300 px-2 py-1.5 rounded-sm inline-flex items-start gap-1.5 max-w-full">
                                                <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                                <span className="break-words">{it.message}</span>
                                            </div>
                                        )}
                                        <div className="text-[10px] text-gray-400 mt-1">Eingegangen: {fmtDate(it.created_at)}</div>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${badge.cls}`}>
                                        {badge.label}
                                    </span>
                                </div>
                                {it.status === "pending" || it.status === "rescheduled" ? (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {STATUS_ACTIONS.map((a) => (
                                            <button
                                                key={a.key}
                                                type="button"
                                                onClick={() => updateStatus(it.id, a.key)}
                                                disabled={busy === `${it.id}-${a.key}`}
                                                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${a.cls} disabled:opacity-60`}
                                                data-testid={`test-ride-action-${a.key}-${it.id}`}
                                            >
                                                <a.icon className="w-3 h-3" /> {a.label}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => updateStatus(it.id, "completed")}
                                            disabled={busy === `${it.id}-completed`}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-60"
                                            data-testid={`test-ride-action-completed-${it.id}`}
                                        >
                                            Erledigt
                                        </button>
                                    </div>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
